"use client";

import { ArrowUpRight, BriefcaseBusiness, Check } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState, type FormEvent, type ReactNode } from "react";

import { persistAppLocaleCookie } from "@/config/app-locale";
import {
  marketingCopy,
  type MarketingLocale,
} from "@/features/marketing/content/marketing-copy";
import { submitDemoRequest } from "@/features/marketing/services/demo-request-service";

import styles from "./marketing-home.module.css";

const LOGIN_PATH = "/12connex/login";

type MarketingHomeProps = {
  locale: MarketingLocale;
};

// The marketing site and the tenant app track locale via separate mechanisms
// (a `lang` query param here vs. the cv_app_locale cookie there), so CTAs that
// cross from one into the other must carry the locale forward explicitly.
function carryLocaleIntoApp(locale: MarketingLocale): void {
  persistAppLocaleCookie(locale);
}

type MarketingChromeProps = MarketingHomeProps & {
  linkToHomeSections?: boolean;
  localePath?: string;
};

export function MarketingHome({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale];

  return (
    <div className={styles.page}>
      <MarketingHeader locale={locale} />

      <main>
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            <div className={styles.heroCopy}>
              <p className={styles.badge}><span aria-hidden="true" />{copy.badge}</p>
              <h1>{copy.title}</h1>
              <p className={styles.description}>{copy.description}</p>
              <div className={styles.ctas}>
                <a href="#demo" className={styles.primaryCta}>
                  {copy.scheduleDemo}<BriefcaseBusiness aria-hidden="true" />
                </a>
                <a href={LOGIN_PATH} className={styles.secondaryCta} onClick={() => carryLocaleIntoApp(locale)}>
                  {copy.createResume}<ArrowUpRight aria-hidden="true" />
                </a>
              </div>
              <ul className={styles.benefits}>
                {copy.benefits.map((benefit) => <li key={benefit}>{benefit}</li>)}
              </ul>
            </div>

            <BuilderMockup locale={locale} />
          </div>
        </section>
        <FeatureSection locale={locale} />
        <WhiteLabelSection locale={locale} />
        <DemoSection locale={locale} />
        <JobSeekerSection locale={locale} />
        <PricingTeaser locale={locale} />
        <AboutSection locale={locale} />
      </main>
      <MarketingFooter locale={locale} />
    </div>
  );
}

function PricingTeaser({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale].pricingTeaser;
  const pricingHref = locale === "fr" ? "/pricing?lang=fr" : "/pricing";

  return (
    <section className={styles.pricingTeaser} aria-label={copy.fullLabel}>
      <div className={styles.pricingTeaserInner}>
        <div className={styles.pricingOption}>
          <strong>{copy.freeLabel}</strong>
          <p>{copy.freeDescription}</p>
        </div>
        <div className={styles.pricingOption}>
          <strong>{copy.fullLabel}</strong>
          <p>{copy.fullDescription}</p>
        </div>
        <Link href={pricingHref} className={styles.pricingTeaserLink}>
          {copy.action}<ArrowUpRight aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
}

function sectionHref(hash: string, locale: MarketingLocale, linkToHomeSections: boolean) {
  return linkToHomeSections ? `/?lang=${locale}${hash}` : hash;
}

function localizedPolicyHref(path: string, locale: MarketingLocale) {
  return locale === "fr" ? `${path}?lang=fr` : path;
}

export function MarketingHeader({
  locale,
  linkToHomeSections = false,
  localePath = "/",
}: MarketingChromeProps) {
  const copy = marketingCopy[locale];

  return (
    <header className={styles.header}>
      <nav className={styles.nav} aria-label="Primary navigation">
        <Link href={`/?lang=${locale}`} className={styles.brand} aria-label="CVBuilder.ca home">
          <Image
            src="/logo/Logo CV Builder.jpg"
            alt=""
            width={48}
            height={48}
            className={styles.logo}
            priority
          />
          <span><strong>CV</strong>Builder<span>.ca</span></span>
        </Link>

        <div className={styles.navLinks}>
          <a href={sectionHref("#features", locale, linkToHomeSections)}>{copy.nav.features}</a>
          <Link href={locale === "fr" ? "/pricing?lang=fr" : "/pricing"}>{copy.nav.pricing}</Link>
          <a href={sectionHref("#demo", locale, linkToHomeSections)}>{copy.nav.demo}</a>
          <a href={sectionHref("#job-seekers", locale, linkToHomeSections)}>{copy.nav.jobSeekers}</a>
          <a href={sectionHref("#about", locale, linkToHomeSections)}>{copy.nav.about}</a>
        </div>

        <div className={styles.navActions}>
          <div className={styles.localeSwitch} aria-label="Language">
            <Link href={`${localePath}?lang=en`} aria-current={locale === "en" ? "page" : undefined}>EN</Link>
            <span>/</span>
            <Link href={`${localePath}?lang=fr`} aria-current={locale === "fr" ? "page" : undefined}>FR</Link>
          </div>
          <a href={sectionHref("#demo", locale, linkToHomeSections)} className={styles.demoButton}>
            {copy.nav.requestDemo}
          </a>
        </div>
      </nav>
    </header>
  );
}

export function MarketingFooter({
  locale,
  linkToHomeSections = false,
}: MarketingChromeProps) {
  const copy = marketingCopy[locale].footer;
  const footerHrefs = [
    [
      sectionHref("#demo", locale, linkToHomeSections),
      sectionHref("#features", locale, linkToHomeSections),
      sectionHref("#about", locale, linkToHomeSections),
    ],
    [LOGIN_PATH, sectionHref("#job-seekers", locale, linkToHomeSections)],
    [
      localizedPolicyHref("/privacy", locale),
      localizedPolicyHref("/terms", locale),
      localizedPolicyHref("/cookies", locale),
      localizedPolicyHref("/refund", locale),
    ],
  ] as const;

  return (
    <footer className={styles.footer}>
      <div className={styles.footerInner}>
        <div className={styles.footerBrand}>
          <Link href={`/?lang=${locale}`}><strong>CV</strong>Builder.ca</Link>
          <p>{copy.description}</p>
          <strong className={styles.hosting}>{copy.hosting}</strong>
        </div>

        {copy.groups.map((group, groupIndex) => (
          <nav className={styles.footerGroup} aria-label={group.title} key={group.title}>
            <h2>{group.title}</h2>
            <ul>
              {group.links.map((label, linkIndex) => (
                <li key={label}>
                  <Link
                    aria-label={
                      groupIndex === 2
                        ? `${label} (${locale === "fr" ? "s’ouvre dans un nouvel onglet" : "opens in a new tab"})`
                        : undefined
                    }
                    href={footerHrefs[groupIndex][linkIndex]}
                    onClick={
                      footerHrefs[groupIndex][linkIndex] === LOGIN_PATH
                        ? () => carryLocaleIntoApp(locale)
                        : undefined
                    }
                    rel={groupIndex === 2 ? "noopener noreferrer" : undefined}
                    target={groupIndex === 2 ? "_blank" : undefined}
                  >
                    <span aria-hidden={groupIndex === 2 ? "true" : undefined}>{label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        ))}

      </div>
      <p className={styles.copyright}>{copy.copyright}</p>
    </footer>
  );
}

function AboutSection({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale].about;

  return (
    <section className={styles.aboutSection} id="about">
      <div className={styles.aboutInner}>
        <div className={styles.aboutGrid}>
          <div className={styles.aboutCopy}>
            <p>{copy.eyebrow}</p>
            <h2>{copy.title}</h2>
            <div>{copy.description}</div>
          </div>

          <div className={styles.aboutPillars}>
            {copy.pillars.map((pillar) => (
              <article key={pillar.title}>
                <h3>{pillar.title}</h3>
                <p>{pillar.description}</p>
              </article>
            ))}
          </div>
        </div>

        <div className={styles.aboutCta}>
          <strong>{copy.prompt}</strong>
          <a href="#demo">{copy.action}<ArrowUpRight aria-hidden="true" /></a>
        </div>
      </div>
    </section>
  );
}

function JobSeekerSection({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale].jobSeekers;

  return (
    <section className={styles.jobSeekerSection} id="job-seekers">
      <div className={styles.jobSeekerInner}>
        <div className={styles.jobSeekerHeading}>
          <div>
            <p>{copy.eyebrow}</p>
            <h2>{copy.title}</h2>
          </div>
          <a href={LOGIN_PATH} onClick={() => carryLocaleIntoApp(locale)}>{copy.action}<ArrowUpRight aria-hidden="true" /></a>
        </div>

        <ol className={styles.jobSeekerSteps}>
          {copy.steps.map((step, index) => (
            <li key={step.title}>
              <span>{locale === "fr" ? "Étape" : "Step"} {index + 1}</span>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function WhiteLabelSection({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale].whiteLabel;

  return (
    <section className={styles.whiteLabelSection} aria-labelledby="white-label-title">
      <div className={styles.whiteLabelInner}>
        <div className={styles.whiteLabelCopy}>
          <p className={styles.whiteLabelEyebrow}>{copy.eyebrow}</p>
          <h2 id="white-label-title">{copy.title}</h2>
          <p>{copy.description}</p>
          <div className={styles.whiteLabelCta}>
            <strong>{copy.prompt}</strong>
            <a href="#demo">{copy.action}<span aria-hidden="true">↓</span></a>
          </div>
        </div>

        <div className={styles.themePreview} aria-label={copy.preview}>
          <div className={styles.themeToolbar}>
            <div className={styles.themeSwatches} aria-hidden="true">
              <span /><span /><span /><span />
            </div>
            <code>{copy.themeLabel}</code>
          </div>
          <div className={styles.themeCanvas}>
            <div className={styles.genericPreview} aria-hidden="true">
              <div className={styles.genericPreviewHeader}><span /><span /></div>
              <div className={styles.genericPreviewBody}>
                <aside><span /><span /><span /><span /></aside>
                <main><span /><strong /><span /><span /><button type="button" tabIndex={-1} /></main>
              </div>
            </div>
            <span className="sr-only">{copy.preview}</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function DemoSection({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale].demoSection;
  const { fields } = copy;
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("submitting");

    const form = event.currentTarget;
    const data = new FormData(form);

    try {
      await submitDemoRequest({
        full_name: String(data.get("full-name") ?? ""),
        organization: String(data.get("organization") ?? ""),
        email: String(data.get("email") ?? ""),
        phone: String(data.get("phone") ?? ""),
        message: String(data.get("message") ?? ""),
        locale,
      });
      form.reset();
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }

  return (
    <section className={styles.demoSection} id="demo">
      <div className={styles.demoInner}>
        <div className={styles.demoCopy}>
          <h2>{copy.title}</h2>
          <p>{copy.description}</p>
          <ul>
            {copy.benefits.map((benefit) => (
              <li key={benefit}><span><Check aria-hidden="true" /></span>{benefit}</li>
            ))}
          </ul>
        </div>

        <form className={styles.demoForm} aria-label={copy.formTitle} onSubmit={handleSubmit}>
          <h3>{copy.formTitle}</h3>
          <DemoField
            id="demo-full-name"
            label={fields.fullName}
            placeholder={fields.fullNamePlaceholder}
            autoComplete="name"
            required
          />
          <DemoField
            id="demo-organization"
            label={fields.organization}
            placeholder={fields.organizationPlaceholder}
            autoComplete="organization"
            required
          />
          <DemoField
            id="demo-email"
            label={fields.email}
            placeholder={fields.emailPlaceholder}
            type="email"
            autoComplete="email"
            required
          />
          <DemoField
            id="demo-phone"
            label={`${fields.phone} (${fields.optional})`}
            placeholder={fields.phonePlaceholder}
            type="tel"
            autoComplete="tel"
          />
          <label className={styles.demoField} htmlFor="demo-message">
            <span>{fields.message} ({fields.optional})</span>
            <textarea id="demo-message" name="message" placeholder={fields.messagePlaceholder} rows={4} />
          </label>
          <button type="submit" className={styles.demoSubmit} disabled={status === "submitting"}>
            {status === "submitting" ? copy.submitting : copy.submit}<BriefcaseBusiness aria-hidden="true" />
          </button>
          {status !== "idle" && status !== "submitting" && (
            <p className={status === "success" ? styles.demoSuccess : styles.demoError} role="status">
              {status === "success" ? copy.success : copy.error}
            </p>
          )}
        </form>
      </div>
    </section>
  );
}

type DemoFieldProps = {
  id: string;
  label: string;
  placeholder: string;
  type?: "email" | "tel" | "text";
  autoComplete: string;
  required?: boolean;
};

function DemoField({
  id,
  label,
  placeholder,
  type = "text",
  autoComplete,
  required = false,
}: DemoFieldProps) {
  return (
    <label className={styles.demoField} htmlFor={id}>
      <span>{label}{required && <span aria-hidden="true"> *</span>}</span>
      <input
        id={id}
        name={id.replace("demo-", "")}
        type={type}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
      />
    </label>
  );
}

const featureIcons = ["🔎", "🍁", "✨", "🗣️", "🔒", "⏱️"] as const;

function FeatureSection({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale].features;

  return (
    <section className={styles.featuresSection} id="features">
      <div className={styles.featuresInner}>
        <p className={styles.featuresEyebrow}>{copy.eyebrow}</p>
        <h2>{copy.title}</h2>
        <div className={styles.featureGrid}>
          {copy.items.map((feature, index) => {
            const icon = featureIcons[index];

            return (
              <article className={styles.featureCard} key={feature.title}>
                <div className={styles.featureMeta}>
                  <span className={styles.featureIcon} aria-hidden="true">{icon}</span>
                  <span className={styles.featureNumber}>{String(index + 1).padStart(2, "0")}</span>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function BuilderMockup({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale].mockup;
  type PreviewSection = "profile" | "achievements" | "experience" | "education" | "additional";
  const [activeSection, setActiveSection] = useState<PreviewSection>("profile");
  const navigation = [
    { id: "profile", label: copy.profile, icon: "👤" },
    { id: "achievements", label: copy.achievements, icon: "✨" },
    { id: "experience", label: copy.experience, icon: "💼" },
    { id: "education", label: copy.education, icon: "🎓" },
    { id: "additional", label: copy.additional, icon: "🗣️" },
  ] satisfies ReadonlyArray<{ id: PreviewSection; label: string; icon: string }>;

  const previewSections: Record<PreviewSection, ReactNode> = {
    profile: (
      <section className={styles.resumePreviewSection}>
        <h2>{copy.profile}</h2>
        <p>{copy.profileText}</p>
      </section>
    ),
    achievements: (
      <section className={styles.resumePreviewSection}>
        <h2>{copy.achievements}</h2>
        <ul>{copy.achievementItems.map((item) => <li key={item}>{item}</li>)}</ul>
      </section>
    ),
    experience: (
      <section className={styles.resumePreviewSection}>
        <h2>{copy.experience}</h2>
        {copy.experienceItems.map((item) => (
          <article className={styles.resumePreviewEntry} key={item.company}>
            <h3>{item.company} — {item.role}</h3>
            <p className={styles.resumePreviewMeta}>{item.location} | {item.dates}</p>
            <ul>{item.details.map((detail) => <li key={detail}>{detail}</li>)}</ul>
          </article>
        ))}
      </section>
    ),
    education: (
      <section className={styles.resumePreviewSection}>
        <h2>{copy.education}</h2>
        {copy.educationItems.map((item) => (
          <article className={styles.resumePreviewEntry} key={item.credential}>
            <h3>{item.credential}</h3>
            <p>{item.institution} | {item.dates}</p>
          </article>
        ))}
      </section>
    ),
    additional: (
      <section className={styles.resumePreviewSection}>
        <h2>{copy.additional}</h2>
        <ul>{copy.additionalItems.map((item) => <li key={item}>{item}</li>)}</ul>
      </section>
    ),
  };

  return (
    <div className={styles.mockup} aria-label="CVBuilder application preview">
      <div className={styles.browserBar}>
        <span /><span /><span />
        <code>cvbuilder.ca / builder</code>
      </div>
      <div className={styles.builder}>
        <aside className={styles.sidebar}>
          <p>{copy.sections}</p>
          <ul role="tablist" aria-label={copy.sections}>
            {navigation.map(({ id, label, icon }) => (
              <li key={id}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeSection === id}
                  aria-controls="marketing-resume-preview"
                  className={activeSection === id ? styles.activeNavItem : undefined}
                  onClick={() => setActiveSection(id)}
                >
                  <span className={styles.previewIcon} aria-hidden="true">{icon}</span>
                  <span className={styles.sidebarLabel}>{label}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className={styles.completion}><span />{copy.complete}</div>
        </aside>
        <div
          id="marketing-resume-preview"
          className={styles.workspace}
          role="tabpanel"
          aria-live="polite"
        >
          {previewSections[activeSection]}
        </div>
      </div>
    </div>
  );
}
